import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { accountingTransferActions } from '../../lib/accountingTransferState';
import { defaultDuettSendOption } from '../../lib/duettSaleStatus';
import { isForeignSale } from '../../lib/crossCustomerSales';

// "Regnskap" section for the sale drawers (Faktura → saleInvoice.js, Betaling →
// salePayment.js). Offers the same per-provider transfer as the "Alle besøk"
// list so a vet can push an untransferred invoice / sale / credit note to
// Fiken, Tripletex, PowerOffice or Duett without leaving the sale. Which
// button (if any) a provider gets is decided by lib/accountingTransferState;
// this component only calls the same store methods the list uses and reloads
// saleStore.newSale afterwards so the section flips to "Overført".
//
// Props: stores (required), onTransferred(provider) (optional).

@observer
class DyrejournalSaleAccountingTransfer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sending: {},
            done: {},
            errors: {},
            messages: {},
            duettSendOption: null,
        };
    }

    // Same rule as allVisitsTable.getTripletexCreateError: Tripletex only
    // created the invoice when response.data.value.id exists — a 200 with an
    // error body must NOT be shown as transferred (TICKET-01186).
    tripletexCreateError = response => {
        const data = (response && response.data) || {};
        if (data.value && data.value.id) {
            return null;
        }
        const messages = [];
        if (response && response.status && response.status !== 200 && response.message) {
            messages.push(response.message);
        }
        if (data.message) {
            messages.push(data.message);
        }
        if (Array.isArray(data.validationMessages)) {
            data.validationMessages.forEach(v => {
                if (v && v.message) {
                    messages.push(v.message);
                }
            });
        }
        if (messages.length === 0 && typeof data.error === 'string' && data.error) {
            messages.push(data.error);
        }
        return messages.join(' - ') || 'Ukjent feil ved overføring til Tripletex';
    }

    // Same wording as sendInvoice.powerOfficeOutcomeText (TICKET-01431): a sales
    // order is not a booked invoice, so say what actually landed in PowerOffice.
    powerOfficeOutcomeText = response => {
        const { workflowType, invoiceCreated, invoiceSentToCustomer } = (response && response.data) || {};
        if (workflowType === 'cashJournal') {
            return 'Ført som kontantsalg i PowerOffice.';
        }
        if (invoiceCreated) {
            return invoiceSentToCustomer
                ? 'Faktura opprettet i PowerOffice og sendt til kunden.'
                : 'Faktura opprettet i PowerOffice, men ikke sendt til kunden. Send den fra PowerOffice.';
        }
        return 'Salgsordre opprettet i PowerOffice. Den må faktureres i PowerOffice for å bli bokført.';
    }

    reloadSale = async saleId => {
        const { saleStore } = this.props.stores;
        const reloaded = await saleStore.load(saleId, false, { skipUpdate: true });
        if (reloaded && reloaded[0]) {
            saleStore.updateKeyValue('newSale', reloaded[0]);
        }
    }

    setProviderState = (provider, patch) => {
        this.setState(prevState => {
            const next = {};
            Object.keys(patch).forEach(key => {
                next[key] = { ...prevState[key], [provider]: patch[key] };
            });
            return next;
        });
    }

    onChangeDuettSendOption = e => {
        this.setState({ duettSendOption: e.target.value });
    }

    onClickTransfer = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { provider, action } = e.target.closest('button').dataset;
        await this.transfer({ provider, action });
    }

    transfer = async ({ provider, action }) => {
        const { saleStore, fikenStore, tripletexStore, powerofficeStore, duettStore } = this.props.stores;
        const { onTransferred = () => {} } = this.props;
        const { newSale } = saleStore;
        const saleId = newSale && newSale.id;
        if (!saleId) {
            return;
        }
        // Guard against double-click: the disabled prop lags a frame behind.
        if (this.state.sending[provider]) {
            return;
        }
        this.setProviderState(provider, { sending: true, errors: null, messages: null });

        let error = null;
        let message = null;
        try {
            switch (provider) {
                case 'tripletex': {
                    const response = action === 'creditNote'
                        ? await tripletexStore.createCreditNote(saleId)
                        : await tripletexStore.createInvoice(saleId);
                    error = this.tripletexCreateError(response);
                    break;
                }
                case 'fiken': {
                    const response = action === 'sale'
                        ? await fikenStore.createCompanySale(saleId)
                        : await fikenStore.createCompanyInvoice(saleId);
                    if (!response || response.status !== 200) {
                        error = (response && response.message) || 'Ukjent feil ved overføring til Fiken';
                    }
                    break;
                }
                case 'powerOffice': {
                    const response = await powerofficeStore.createInvoiceFromSale(saleId);
                    if (!response || response.status !== 200) {
                        error = (response && response.message) || 'Ukjent feil ved overføring til PowerOffice';
                    } else {
                        message = this.powerOfficeOutcomeText(response);
                    }
                    break;
                }
                case 'duett': {
                    let response;
                    if (action === 'creditNote') {
                        response = await duettStore.createCreditNote(saleId);
                    } else {
                        const visitor = newSale.visitors && newSale.visitors[0];
                        const invoiceSendOption = this.state.duettSendOption || defaultDuettSendOption(visitor);
                        response = await duettStore.createInvoice(saleId, { invoiceSendOption });
                    }
                    if (!response || response.status >= 300) {
                        // Backend messages already include Duett's errorMessages verbatim.
                        error = (response && response.message) || 'Ukjent feil ved sending til Duett';
                    }
                    break;
                }
                default:
                    error = 'Ukjent regnskapssystem';
            }
        } catch (err) {
            error = (err && err.message) || String(err);
        }

        if (!error) {
            await this.reloadSale(saleId);
            onTransferred(provider);
        }
        this.setProviderState(provider, { sending: false, done: !error, errors: error, messages: message });
    }

    renderActionLabel = action => {
        switch (action) {
            case 'sale':
                return <Text id='dyrejournal.accounting-create-sale'>Create sale</Text>;
            case 'creditNote':
                return <Text id='dyrejournal.accounting-create-creditNote'>Create credit note</Text>;
            default:
                return <Text id='dyrejournal.accounting-create-invoice'>Create invoice</Text>;
        }
    }

    renderTransferred = () => (
        <span class='text-success'>
            <i class='fa-solid fa-check-circle' /> <Text id='dyrejournal.accounting-transferred'>Transferred</Text>
        </span>
    );

    renderRow = row => {
        const { saleStore } = this.props.stores;
        const { newSale = {} } = saleStore;
        const { sending, done, errors, messages, duettSendOption } = this.state;
        const { provider, action, state, reason, voucherNumber } = row;

        if (done[provider]) {
            return <>
                {this.renderTransferred()}
                {messages[provider] && <div class='w-100 text-center small text-body-secondary'>{messages[provider]}</div>}
            </>;
        }
        switch (state) {
            case 'unconfigured':
                return (
                    <small class='text-body-secondary'>
                        <i class='fa-solid fa-key text-warning' /> <Text id='dyrejournal.accounting-transfer-unconfigured'>Integration is not fully configured</Text>
                    </small>
                );
            case 'blocked':
                return (
                    <small class='text-body-secondary'>
                        <i class='fa-solid fa-ban' /> {reason}
                    </small>
                );
            case 'transferred':
                return this.renderTransferred();
            case 'inVoucher':
                return (
                    <small class='text-body-secondary'>
                        <i class='fa-solid fa-file-invoice' /> <Text id='dyrejournal.accounting-in-voucher'>In voucher</Text>{voucherNumber ? ` #${voucherNumber}` : ''}
                    </small>
                );
            case 'viaVoucher':
                return (
                    <small class='text-body-secondary'>
                        <Text id='dyrejournal.accounting-via-voucher'>Transferred via the daily settlement voucher</Text>
                    </small>
                );
            case 'ready': {
                const isSending = !!sending[provider];
                const buttonClass = action === 'creditNote' ? 'btn-warning' : (action === 'sale' ? 'btn-outline-info' : 'btn-info');
                return <>
                    <button
                        type='button'
                        class={`btn btn-sm ${buttonClass} rounded-pill`}
                        style='pointer-events: auto;'
                        disabled={isSending}
                        data-provider={provider}
                        data-action={action}
                        onClick={this.onClickTransfer}
                    >
                        {isSending ? <>
                            <i class='fa-solid fa-spinner-third fa-spin' /> <Text id='dyrejournal.accounting-transferring'>Transferring...</Text>
                        </> : <>
                            <i class='fa-duotone fa-cloud-arrow-up' /> {this.renderActionLabel(action)}
                        </>}
                    </button>
                    {provider === 'duett' && action === 'invoice' && (
                        <select
                            class='form-select form-select-sm d-inline-block'
                            style='width: auto;'
                            value={duettSendOption || defaultDuettSendOption(newSale.visitors && newSale.visitors[0])}
                            onInput={this.onChangeDuettSendOption}
                            disabled={isSending}
                        >
                            <option value='email'><Text id='dyrejournal.accounting-duett-send-email'>E-mail</Text></option>
                            <option value='autoTrans'><Text id='dyrejournal.accounting-duett-send-ehf'>EHF</Text></option>
                            <option value='print'><Text id='dyrejournal.accounting-duett-send-print'>Print (sent from Duett)</Text></option>
                        </select>
                    )}
                    {errors[provider] && (
                        <div class='w-100 text-center small text-danger'>
                            <i class='fa-solid fa-triangle-exclamation' /> {errors[provider]}
                        </div>
                    )}
                </>;
            }
            default:
                return null;
        }
    }

    render() {
        const { saleStore, userStore } = this.props.stores;
        const { customer = {} } = userStore;
        const { newSale = {} } = saleStore;
        if (!newSale.id || isForeignSale(newSale, userStore)) {
            return null;
        }
        const rows = accountingTransferActions(newSale, customer);
        if (rows.length === 0) {
            return null;
        }
        return (
            <div class='w-100 d-flex flex-column align-items-center mt-4 pb-3 border-bottom'>
                <div class='font-weight-lighter box-header'>
                    <i class='fa-duotone fa-building-columns me-2' /><Text id='dyrejournal.accounting-transfer'>Accounting</Text>
                </div>
                <small class='text-body-secondary mb-2 text-center'>
                    <Text id='dyrejournal.accounting-transfer-help'>Transfer this sale to your accounting system from here, without going to the sale list.</Text>
                </small>
                {rows.map(row => (
                    <div key={row.provider} class='d-flex flex-row flex-wrap align-items-center justify-content-center gap-2 mb-2 w-100'>
                        <span class='fw-bold text-end' style='min-width: 6em;'>{row.providerName}</span>
                        {this.renderRow(row)}
                    </div>
                ))}
            </div>
        );
    }
}

export default DyrejournalSaleAccountingTransfer;
